import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useAuth } from '@restworld/feature-authentication';
import { registerSuperProperties } from '@restworld/utils-common';
import { isOrgOnboardingModeAtom, orgOnboardingPartnerInfoAtom } from './use-organization-onboarding';
import { isDeferredBillingCampaign } from './deferred-billing';

/**
 * The Try for Free A/B test on /getting-started (RW-411, Luca, Oct 2026).
 *
 * Only visitors whose partner code is a deferred billing campaign (Try for Free) take part:
 * right after the partner lookup they are split 50/50 between `default` (today's flow) and
 * `simplified` (the changes under test). Everyone else never gets a variant, so for them
 * nothing changes. The variant is kept in its own browser storage key, outside the onboarding
 * state, so a reload, «Ricomincia» and the end of the onboarding all keep it. The same values
 * go to the prospect (`flow_variant`) and to every PostHog event, so the test can be read
 * from both.
 */
export type TryForFreeVariant = 'default' | 'simplified';

export interface TryForFreeAssignment {
  variant: TryForFreeVariant;
  partner_code: string;
}

// Versioned: a change to what the variants mean bumps it, so nobody carries an old
// assignment into a new test.
export const TRY_FOR_FREE_VARIANT_STORAGE_KEY = 'try-for-free-variant-v1';

export const tryForFreeAssignmentAtom = atomWithStorage<TryForFreeAssignment | null>(
  TRY_FOR_FREE_VARIANT_STORAGE_KEY,
  null,
  undefined,
  { getOnInit: true }
);

export const pickTryForFreeVariant = (): TryForFreeVariant => (Math.random() < 0.5 ? 'default' : 'simplified');

/**
 * Option 2: role, contract type and job type as buttons instead of dropdowns. Follows the
 * visitor wherever this browser carries the `simplified` variant, the logged-in wizard and the
 * editing of an ad included, so they keep seeing what they saw in the onboarding.
 */
export const useTryForFreeButtons = () => useAtomValue(tryForFreeAssignmentAtom)?.variant === 'simplified';

/**
 * Option 3: skills, benefits and additional notes are skipped. Only for a new venue in the
 * Try for Free onboarding (not logged in); a logged-in employer gets the whole flow.
 */
export const useTryForFreeShortFlow = () => {
  const { isAuthenticated } = useAuth();
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);
  const partnerInfo = useAtomValue(orgOnboardingPartnerInfoAtom);
  const assignment = useAtomValue(tryForFreeAssignmentAtom);
  return (
    isOrgOnboarding &&
    !isAuthenticated &&
    isDeferredBillingCampaign(partnerInfo) &&
    assignment?.variant === 'simplified'
  );
};

/**
 * Puts the visitor's partner code and variant on every PostHog event of the page. Mounted once
 * in `_app`, so the pages after the onboarding (payment, publication) carry them too.
 */
export const useTryForFreeAnalyticsTags = () => {
  const assignment = useAtomValue(tryForFreeAssignmentAtom);

  useEffect(() => {
    if (!assignment) return;
    registerSuperProperties({ partner_code: assignment.partner_code, flow_variant: assignment.variant });
  }, [assignment]);
};
